import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  FileText,
  Calendar,
  User,
  Eye,
  Edit,
  Trash2,
  FolderOpen,
  Grid3X3,
  List,
  MoreVertical,
  Star,
  Clock,
  CheckCircle2
} from "lucide-react";
import { message } from "antd";
import { formatJsDate } from "../utils/formatageDate";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axios/AxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Label } from "../ui/Label";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectGroup,
  SelectLabel,
  SelectValue,
} from "../ui/Select";

const documentsInitiaux = [
  {
    id: "doc1",
    titre: "Livret d'accueil pour les permanents",
    categorie: "Ressources humaines",
    dateDepot: "25/01/2024",
    deposePar: "Martine Dubois",
    fichier: "/documents/livret_accueil_permanents.pdf",
  },
  {
    id: "doc2",
    titre: "Conditions générales de placement / vacation",
    categorie: "Juridique",
    dateDepot: "25/01/2024",
    deposePar: "Philippe Martin",
    fichier: "/documents/conditions_generales.pdf",
  },
  {
    id: "doc3",
    titre: "Livret d'accueil et de sécurité pour les intérimaires",
    categorie: "Ressources humaines",
    dateDepot: "25/01/2024",
    deposePar: "Martine Dubois",
    fichier: "/documents/livret_securite_interim.pdf",
  },
  {
    id: "doc4",
    titre: "Charte RSE du Groupe J'm interim",
    categorie: "RSE",
    dateDepot: "25/01/2024",
    deposePar: "Laurent Gérard",
    fichier: "/documents/charte_rse.pdf",
  },
  {
    id: "doc5",
    titre: "Lecture d'un bulletin de paie",
    categorie: "Formation",
    dateDepot: "25/02/2024",
    deposePar: "Sophie Bernard",
    fichier: "/documents/lecture_bulletin_paie.pdf",
  },
  {
    id: "doc6",
    titre: "Demander un acompte",
    categorie: "Finance",
    dateDepot: "25/02/2024",
    deposePar: "Nicolas Petit",
    fichier: "/documents/procedure_acompte.pdf",
  },
  {
    id: "doc7",
    titre: "Enregistrer un candidat",
    categorie: "Recrutement",
    dateDepot: "25/02/2024",
    deposePar: "Claire Moreau",
    fichier: "/documents/procedure_enregistrement_candidat.pdf",
  },
  {
    id: "doc8",
    titre: "Enregistrer un candidat disponible sans CV",
    categorie: "Recrutement",
    dateDepot: "25/02/2024",
    deposePar: "Claire Moreau",
    fichier: "/documents/procedure_candidat_sans_cv.pdf",
  },
];

const categories = [
  "Toutes",
  "Admin/PLD",
  "Commercial",
  "Formation",
  "Juridique",
  "Livret",
  "Paie",
  "Ressources Humaines",
  "RSE",
  "Autre",
];

const BibliothequePage = () => {
  const [filteredDocuments, setFilteredDocuments] = useState(documentsInitiaux);
  const [searchTerm, setSearchTerm] = useState("");
  const [categorieSelectionnee, setCategorieSelectionnee] = useState("Toutes");
  const [currentPage, setCurrentPage] = useState(1);
  const documentsPerPage = 12;
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);
  const [nouveauDocument, setNouveauDocument] = useState({
    titre: "",
    categorie: "",
    fichier: "",
  });
  const [isAddDocument, setIsAddDocument] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const inputFileRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [showDocument, setShowDocument] = useState(false);
  const [documentData, setDocumentData] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [numPages, setNumPages] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const navigate = useNavigate();

  // Calcul des statistiques
  const stats = {
    total: documents.length,
    categories: [...new Set(documents.map(doc => doc.categorie))].length,
    recent: documents.filter(doc => {
      const docDate = new Date(doc.dateDepot);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return docDate > weekAgo;
    }).length
  };

  // Filtrage des documents
  const getFilteredDocuments = () => {
    let filtered = documents;

    if (categorieSelectionnee !== "Toutes") {
      filtered = filtered.filter(doc => doc.categorie === categorieSelectionnee);
    }

    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.categorie.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.deposePar.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredDocs = getFilteredDocuments();
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  const base64toBlob = (base64String, contentType = "application/pdf") => {
    const byteCharacters = window.atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  const handleClick = () => {
    inputFileRef.current.click();
  };
  const handleAddDocument = () => {
    if (nouveauDocument.titre && nouveauDocument.categorie) {
      const newDoc = {
        id: `doc${documents.length + 1}`,
        titre: nouveauDocument.titre,
        categorie: nouveauDocument.categorie,
        dateDepot: new Date().toLocaleDateString("fr-FR"),
        deposePar: "Utilisateur actuel",
        fichier: nouveauDocument.fichier || "/documents/default.pdf",
      };
      const updatedDocs = [...documents, newDoc];
      setDocuments(updatedDocs);
      setFilteredDocuments(updatedDocs);
      setNouveauDocument({ titre: "", categorie: "", fichier: "" });
      setIsAddDocumentOpen(false);
    }
  };

  const supprimerDocument = (id) => {
    const updatedDocs = documents.filter((doc) => doc.id !== id);
    setDocuments(updatedDocs);
    setFilteredDocuments(updatedDocs);
  };

  const getDocument = (documentId) => {
    axiosInstance
      .get(`/global-doc/${documentId}`)
      .then((res) => {
        const pdfBlob = base64toBlob(res.data.data);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      })
      .catch((e) => message.error("Une erreur s'est produite"));
  };
  const deleteDocument = (documentId, idx) => {
    axiosInstance
      .delete(`/global-doc/freelance/${documentId}`)
      .then((res) => {
        const newDocuments = [...documents];
        newDocuments.splice(idx, 1);
        setDocuments(newDocuments);
        message.success("Document supprimé !");
      })
      .catch((e) => message.error("Une erreur s'est produite"));
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    const newArray = Array.from({ length: numPages }, (_, index) => index + 1);
    setNumPages(newArray);
  };

  const findByName = (query) => {
    axiosInstance
      .post("global-doc/freelance/find-by-name", { query })
      .then((res) => {
        setDocuments(res.data);
      })
      .catch((e) => console.log(e));
  };

  const addDocument = () => {
    setIsAddingDocument(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("name", documentName);
    formData.append("category", categorieSelectionnee);

    axiosInstance
      .post("/global-doc", formData)
      .then((res) => {
        message.success("Document ajouté !");
        const newDocuments = [...documents, res.data];
        setDocuments(newDocuments);
        setIsAddDocumentOpen(false);
      })
      .catch((e) => {
        setSelectedFile(null);
        console.log(e);
        if (e.response.data.statusCode === 403) {
          localStorage.clear();
          navigate("/login");
        }
      })
      .finally(() => {
        setIsAddingDocument(false);
      });
  };

  const loadDocuments = () => {
    setIsLoadingDocuments(true);
    axiosInstance
      .get("global-doc/freelance")
      .then((res) => {
        setDocuments(res.data);
        console.log(res.data);
        setIsLoadingDocuments(false);
      })
      .catch((e) => {
        setIsLoadingDocuments(false);
        message.error("une erreur s'est produite");
      });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file.type === "application/pdf") {
      setSelectedFile(event.target.files[0]);
    } else {
      message.error("Veuillez ajouter un fichier pdf !");
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 mb-8"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-blue-100 p-4 rounded-xl">
                <BookOpen className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">Bibliothèque de Documents</h1>
                <p className="text-slate-600 mt-1">Gérez et organisez tous vos documents importants</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsAddDocumentOpen(true)}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors duration-200 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span>Nouveau document</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total documents</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Catégories</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats.categories}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <FolderOpen className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Ajoutés récemment</p>
                <p className="text-3xl font-bold text-orange-500 mt-1">{stats.recent}</p>
                <p className="text-sm text-slate-500 mt-1">Cette semaine</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un document..."
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 w-full"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4">
              <select
                value={categorieSelectionnee}
                onChange={(e) => setCategorieSelectionnee(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2 bg-slate-100 rounded-lg p-1">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === "grid"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-lg transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <List className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Documents List/Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {isLoadingDocuments ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun document trouvé</h3>
              <p className="text-slate-500">
                {searchTerm ? "Essayez de modifier vos critères de recherche" : "Commencez par ajouter votre premier document"}
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <AnimatePresence>
                {filteredDocs.map((doc, index) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 cursor-pointer hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-1 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </motion.button>
                    </div>

                    <div className="space-y-3">
                      <h3 className="font-bold text-lg text-slate-900 line-clamp-2">
                        {doc.titre}
                      </h3>

                      <div className="flex items-center text-sm text-slate-600">
                        <FolderOpen className="w-4 h-4 mr-2 text-slate-400" />
                        <span>{doc.categorie}</span>
                      </div>

                      <div className="flex items-center text-sm text-slate-600">
                        <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                        <span>{doc.dateDepot}</span>
                      </div>

                      <div className="flex items-center text-sm text-slate-600">
                        <User className="w-4 h-4 mr-2 text-slate-400" />
                        <span>{doc.deposePar}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-200"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Document
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Catégorie
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Déposé par
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    <AnimatePresence>
                      {filteredDocs.map((doc, index) => (
                        <motion.tr
                          key={doc.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-slate-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="bg-slate-100 p-2 rounded-lg mr-3">
                                <FileText className="w-4 h-4 text-slate-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-slate-900">
                                  {doc.titre}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {doc.categorie}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {doc.dateDepot}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {doc.deposePar}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                                title="Voir"
                              >
                                <Eye className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                title="Télécharger"
                              >
                                <Download className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-200"
                                title="Modifier"
                              >
                                <Edit className="w-4 h-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={inputFileRef}
        onChange={(e) => setSelectedFile(e.target.files[0])}
        style={{ display: "none" }}
        accept=".pdf,.doc,.docx,.txt"
      />

      {/* Add Document Modal */}
      {isAddDocumentOpen && (
        <Dialog
          open={isAddDocumentOpen}
          onOpenChange={setIsAddDocumentOpen}
          className="bg-white"
        >
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>Ajouter un nouveau document</DialogTitle>
            </DialogHeader>

            <div className="grid gap-4 py-4 bg-white">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="titre" className="text-right font-bold">
                  Titre
                </Label>
                <Input
                  placeholder="Titre du document"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  className="col-span-3"
                />
              </div>

              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="categorie" className="text-right font-bold">
                  Catégorie
                </Label>
                <Select
                  value={categorieSelectionnee}
                  onValueChange={(value) => setCategorieSelectionnee(value)}
                >
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {categories
                      .filter((cat) => cat !== "Toutes")
                      .map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-4 items-center gap-4 text-black">
                <Label htmlFor="fichier" className="text-right font-bold">
                  Fichier
                </Label>
                <Input
                  id="fichier"
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="col-span-3 bg-gray-200 text-black"
                  ref={inputFileRef}
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-4">
              <Button
                variant="outline"
                onClick={() => setIsAddDocumentOpen(false)}
              >
                Annuler
              </Button>
              <Button
                onClick={() => {
                  // Add document logic here
                  setIsAddDocumentOpen(false);
                }}
              >
                Ajouter
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BibliothequePage;
